import React, { useState, useEffect, useMemo } from 'react';
import toast from 'react-hot-toast';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {
  Users,
  UserPlus,
  Search,
  Trash2,
  Edit2,
  Mail,
  Shield,
  RefreshCw,
  ChevronsDown,
  X,
  User,
  CheckCircle2,
  XCircle,
} from 'lucide-react';

import { DataState } from '../../context/DataProvider';
import { useAccess } from '../../services/access';
import { API_URL } from '../../services/api';
import { appImages } from '../../utils/constants';
import {
  ENABLE_INTEGRATION_JIRA,
  ENABLE_INTEGRATION_CONFLUENCE,
  ENABLE_INTEGRATION_MICROSOFT_TEAMS,
  ENABLE_INTEGRATION_MICROSOFT_OUTLOOK,
  ENABLE_INTEGRATION_MICROSOFT_TODO,
} from '../../config/features';
import { confirmDialog } from '../ConfirmDialog';

// --- Compact Styles for React Select ---
const menuProps = { menuPortalTarget: document.body, menuPosition: 'fixed' };

const selectStyles = {
  // Menus render on <body> (menuProps) so the scrolling form and table
  // do not cut them off; this keeps them above the modal.
  menuPortal: (styles) => ({ ...styles, zIndex: 9999 }),
  control: (styles) => ({
    ...styles,
    backgroundColor: '#27272a', // zinc-800
    borderColor: '#3f3f46', // zinc-700
    boxShadow: 'none',
    '&:hover': { borderColor: '#52525b' }, // zinc-600
    minHeight: '36px', // Compact height
    borderRadius: '0.375rem', // rounded-md
    fontSize: '0.875rem',
  }),
  menu: (styles) => ({
    ...styles,
    backgroundColor: '#18181b', // zinc-950
    borderColor: '#3f3f46',
    borderWidth: '1px',
    borderRadius: '0.375rem',
    padding: '0.25rem',
    zIndex: 50,
  }),
  option: (styles, { isFocused, isSelected }) => ({
    ...styles,
    backgroundColor: isSelected ? '#2563eb' : isFocused ? '#27272a' : '#18181b',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.75rem', // Smaller font
    padding: '0.375rem 0.75rem',
    borderRadius: '0.25rem',
  }),
  singleValue: (styles) => ({
    ...styles,
    color: '#e4e4e7',
    fontSize: '0.875rem',
  }),
  input: (styles) => ({ ...styles, color: 'white', margin: 0, padding: 0 }),
  valueContainer: (styles) => ({ ...styles, padding: '0 8px' }),
  placeholder: (styles) => ({
    ...styles,
    color: '#a1a1aa',
    fontSize: '0.875rem',
  }),
};

// --- UI Helper Components ---

const Avatar = ({ name }) => {
  const initials = name
    ? name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    : '??';

  const colors = [
    'bg-blue-600',
    'bg-emerald-600',
    'bg-purple-600',
    'bg-amber-600',
    'bg-rose-600',
  ];
  const colorClass = colors[name ? name.length % colors.length : 0];

  return (
    <div
      className={`h-8 w-8 rounded-full ${colorClass} flex items-center justify-center text-xs font-bold text-white shadow-sm ring-1 ring-zinc-900`}
    >
      {initials}
    </div>
  );
};

const AppBadge = ({ appKey, data }) => {
  if (!data) return null;
  const appNames = {
    jira: 'Jira',
    confluence: 'Confluence',
    microsoft_teams: 'Teams',
    microsoft_outlook: 'Outlook',
    microsoft_todo: 'To-Do',
    slack: 'Slack',
    gmail: 'Gmail',
  };

  return (
    <div className="flex items-center gap-1.5 rounded border border-zinc-700/50 bg-zinc-800/80 px-2 py-0.5 text-[11px] text-zinc-300">
      {appImages[appKey] ? (
        <img
          src={appImages[appKey]}
          alt={appKey}
          className="h-3 w-3 object-contain"
        />
      ) : (
        <div className="h-1.5 w-1.5 rounded-full bg-[#5652CF]"></div>
      )}
      <span className="max-w-[100px] truncate">
        {data.displayName || data.email || appNames[appKey]}
      </span>
    </div>
  );
};

const UserTableRowSkeleton = () => (
  <tr className="animate-pulse border-b border-zinc-800/50">
    <td className="px-6 py-3">
      <div className="h-8 w-8 rounded-full bg-zinc-800"></div>
    </td>
    <td className="px-6 py-3">
      <div className="mb-1 h-4 w-32 rounded bg-zinc-800"></div>
      <div className="h-3 w-20 rounded bg-zinc-800"></div>
    </td>
    <td className="px-6 py-3">
      <div className="flex gap-2">
        <div className="h-6 w-16 rounded bg-zinc-800"></div>
      </div>
    </td>
    <td className="px-6 py-3 text-right">
      <div className="ml-auto h-7 w-7 rounded bg-zinc-800"></div>
    </td>
  </tr>
);

// --- Main Component ---

const UserManagementContent = () => {
  const { userGoogleId } = DataState();
  // Each button only for people the backend lets do it.
  const { can } = useAccess();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const [isBulkMapOpen, setIsBulkMapOpen] = useState(false);
  const [mappableAccounts, setMappableAccounts] = useState({
    jira_users: [],
    confluence_users: [],
    teams_users: [],
    outlook_users: [],
    todo_users: [],
  });

  const fetchAllData = async () => {
    setLoading(true);
    try {
      const [usersRes, mappableRes] = await Promise.all([
        fetch(`${API_URL}/users/`, {
          headers: { Authorization: `Bearer ${userGoogleId}` },
          credentials: 'include',
        }),
        fetch(`${API_URL}/users/mappable_accounts`, {
          headers: { Authorization: `Bearer ${userGoogleId}` },
          credentials: 'include',
        }),
      ]);

      if (!usersRes.ok) throw new Error('Failed to load user list.');

      const usersData = await usersRes.json();
      const mappableData = mappableRes.ok
        ? await mappableRes.json()
        : {
            jira_users: [],
            confluence_users: [],
            teams_users: [],
            outlook_users: [],
            todo_users: [],
          };

      setUsers(usersData);
      setMappableAccounts(mappableData);
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userGoogleId]);

  const handleAddUser = () => {
    setEditingUser({ displayName: '', email: '', role: '', app_mappings: {} });
    setIsFormOpen(true);
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setIsFormOpen(true);
  };

  const handleDeleteUser = async (userId) => {
    if (
      await confirmDialog({
        title: 'Delete this user?',
        message: 'This cannot be undone.',
        confirmLabel: 'Delete',
        danger: true,
      })
    ) {
      try {
        const res = await fetch(`${API_URL}/users/${userId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${userGoogleId}` },
          credentials: 'include',
        });
        if (!res.ok) throw new Error('Failed to delete user.');
        toast.success('User deleted.');
        fetchAllData();
      } catch (err) {
        toast.error(err.message);
      }
    }
  };

  const filteredUsers = useMemo(() => {
    if (!searchQuery) return users;
    const lowerQuery = searchQuery.toLowerCase();
    return users.filter(
      (user) =>
        (user.displayName &&
          user.displayName.toLowerCase().includes(lowerQuery)) ||
        (user.email && user.email.toLowerCase().includes(lowerQuery)),
    );
  }, [users, searchQuery]);

  return (
    <div className="flex h-full max-h-screen flex-col p-1">
      {/* --- Outer Header Area --- */}
      <div className="mb-4 flex flex-col gap-3 px-1 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-indigo-600/10 text-indigo-400">
            <Users className="h-5 w-5 text-[#5652CF]" />
          </div>
          <div>
            <h3 className="text-base font-bold leading-tight text-zinc-100">
              Team Directory
            </h3>
            <p className="text-xs text-zinc-400">
              Manage team members, roles, and integration accounts.
            </p>
          </div>
        </div>

        {/* Action Controls */}
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-zinc-800/80 px-2.5 py-1 text-[11px] font-medium text-zinc-400">
            <strong className="text-zinc-200">{users.length}</strong> Users
          </span>

          <button
            onClick={() => fetchAllData()}
            disabled={loading}
            className="flex h-8 w-8 items-center justify-center rounded-md border border-zinc-700/60 bg-zinc-800/60 text-zinc-400 transition-all hover:bg-zinc-700 hover:text-zinc-100 active:scale-95 disabled:opacity-50"
            title="Refresh Directory"
          >
            <RefreshCw
              className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`}
            />
          </button>

          {can('user_management:update') && (
            <button
              onClick={() => setIsBulkMapOpen(true)}
              className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md border border-indigo-500/40 bg-indigo-600/15 px-3 text-xs font-medium text-indigo-300 transition-all hover:bg-indigo-600/25 active:scale-95"
              title="Import and sync users from Jira, Teams, Outlook, etc."
            >
              <RefreshCw className="h-3 w-3 text-indigo-400" />
              <span>Sync Users</span>
            </button>
          )}

          {can('user_management:create') && (
            <button
              onClick={handleAddUser}
              className="flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md bg-[#5652CF] px-3.5 text-xs font-semibold text-white shadow-sm transition-all hover:bg-[#4642bf] active:scale-95"
            >
              <UserPlus className="h-3.5 w-3.5" />
              <span>Add User</span>
            </button>
          )}
        </div>
      </div>

      {/* --- Content Table Card --- */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg border border-zinc-800 bg-[#121214] shadow-sm">
        {/* Search Toolbar inside Card */}
        <div className="border-b border-zinc-800 bg-zinc-900/40 px-3 py-2.5">
          <div className="relative max-w-sm">
            <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-zinc-500" />
            <input
              type="text"
              placeholder="Search by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="h-8 w-full rounded-md border border-zinc-700/70 bg-zinc-900/90 py-1 pl-8 pr-3 text-xs text-zinc-200 transition-all placeholder:text-zinc-500 focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/40"
            />
          </div>
        </div>

        {/* Compact Table */}
        <div className="custom-scrollbar flex-1 overflow-auto bg-zinc-900/10">
          <table className="w-full border-collapse text-left">
            <thead className="sticky top-0 z-10 border-b border-zinc-800 bg-zinc-900/90 backdrop-blur-sm">
              <tr>
                <th className="w-14 px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  Avatar
                </th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  User Details
                </th>
                <th className="px-6 py-3 text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  Integrations
                </th>
                <th className="px-6 py-3 text-right text-[10px] font-bold uppercase tracking-wider text-zinc-500">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800/50">
              {loading ? (
                <>
                  <UserTableRowSkeleton />
                  <UserTableRowSkeleton />
                  <UserTableRowSkeleton />
                </>
              ) : filteredUsers.length === 0 ? (
                <tr>
                  <td colSpan="4" className="py-16 text-center">
                    <div className="flex flex-col items-center justify-center text-zinc-500">
                      <div className="mb-3 rounded-full bg-zinc-800/50 p-3">
                        <User className="h-6 w-6 opacity-50" />
                      </div>
                      <p className="text-sm font-medium text-zinc-400">
                        No users found
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                filteredUsers.map((user) => (
                  <tr
                    key={user.id}
                    className="group transition-colors hover:bg-zinc-800/30"
                  >
                    <td className="px-6 py-3 align-top">
                      <Avatar name={user.displayName} />
                    </td>
                    <td className="px-6 py-3 align-top">
                      <div className="flex flex-col">
                        <span className="text-sm font-medium leading-tight text-zinc-200 transition-colors group-hover:text-blue-400">
                          {user.displayName || 'Unnamed'}
                        </span>
                        <div className="mt-0.5 flex items-center gap-1.5 text-xs text-zinc-500">
                          <Mail className="h-3 w-3" />
                          {user.email}
                        </div>
                        {user.role && (
                          <div className="mt-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-zinc-600">
                            <Shield className="h-3 w-3" /> {user.role}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 align-top">
                      <div className="flex flex-wrap gap-1.5">
                        {Object.keys(user.app_mappings || {}).length === 0 && (
                          <span className="text-xs italic text-zinc-600">
                            No links
                          </span>
                        )}
                        {Object.entries(user.app_mappings || {}).map(
                          ([key, data]) => (
                            <AppBadge key={key} appKey={key} data={data} />
                          ),
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-right align-middle">
                      <div className="flex items-center justify-end gap-1 opacity-100 transition-opacity md:opacity-0 md:group-hover:opacity-100">
                        {can('user_management:update') && (
                          <button
                            onClick={() => handleEditUser(user)}
                            className="rounded-md p-1.5 text-zinc-400 transition-all hover:bg-[#5652CF]/10 hover:text-[#5652CF]"
                            title="Edit"
                          >
                            <Edit2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                        {can('user_management:delete') && (
                          <button
                            onClick={() => handleDeleteUser(user.id)}
                            className="rounded-md p-1.5 text-zinc-400 transition-all hover:bg-red-500/10 hover:text-red-400"
                            title="Delete"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* --- Modals --- */}
      {isFormOpen && (
        <UserForm
          user={editingUser}
          allUsers={users}
          mappableAccounts={mappableAccounts}
          onClose={() => setIsFormOpen(false)}
          onSave={() => {
            setIsFormOpen(false);
            fetchAllData();
          }}
        />
      )}

      {isBulkMapOpen && (
        <BulkMapModal
          allUsers={users}
          mappableAccounts={mappableAccounts}
          onClose={() => setIsBulkMapOpen(false)}
          onSave={() => {
            setIsBulkMapOpen(false);
            fetchAllData();
          }}
        />
      )}
    </div>
  );
};

// --- Standard Role Definitions ---
// No "Admin": a job title here grants nothing, and that name reads as if it did.
const STANDARD_ROLES = [
  'Developer',
  'Project Manager',
  'QA Analyst',
  'Designer',
];

// The standard roles, then every other role already used in this project's
// directory, so a typed-in role is offered again. A role is a job title for
// the AI only; it grants nothing.
export const projectRoleOptions = (users = []) => {
  const custom = users
    .map((u) => u.role?.trim())
    .filter((r) => r && !STANDARD_ROLES.includes(r))
    .sort((a, b) => a.localeCompare(b));
  return [...new Set([...STANDARD_ROLES, ...custom])].map((r) => ({
    value: r,
    label: r,
  }));
};

const formatCreateLabel = (input) => `Use "${input.trim()}"`;

// --- Compact User Form Modal ---
const UserForm = ({ user, allUsers, onClose, onSave, mappableAccounts }) => {
  const { userGoogleId } = DataState();
  const [formData, setFormData] = useState(user);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState('');

  const roleOptions = projectRoleOptions(allUsers);

  useEffect(() => {
    if (!formData.email) {
      setEmailError('');
      return;
    }
    const isDuplicate = allUsers.some(
      (existingUser) =>
        existingUser.email.toLowerCase() === formData.email.toLowerCase() &&
        existingUser.id !== user.id,
    );
    if (isDuplicate) {
      setEmailError('Email already exists.');
    } else {
      setEmailError('');
    }
  }, [formData.email, allUsers, user.id]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleRoleChange = (selectedOption) => {
    setFormData({
      ...formData,
      role: selectedOption ? selectedOption.value.trim() : '',
    });
  };

  const handleMappingChange = (appName, selectedOption) => {
    const newMappings = { ...formData.app_mappings };
    if (selectedOption) {
      const originalUser = selectedOption.originalUser;
      if (appName === 'jira') {
        newMappings.jira = {
          accountId: originalUser.accountId,
          displayName: originalUser.displayName,
          emailAddress: originalUser.emailAddress,
        };
      } else if (appName === 'confluence') {
        newMappings.confluence = {
          accountId: originalUser.accountId || originalUser.id,
          displayName: originalUser.displayName,
          email: originalUser.email || originalUser.emailAddress,
        };
      } else if (appName === 'microsoft_teams') {
        newMappings.microsoft_teams = {
          userId: originalUser.userId || originalUser.id,
          displayName: originalUser.displayName,
          email: originalUser.email || originalUser.userPrincipalName,
        };
      } else if (appName === 'microsoft_outlook') {
        newMappings.microsoft_outlook = {
          userId: originalUser.userId || originalUser.id,
          email: originalUser.email || originalUser.userPrincipalName,
        };
      } else if (appName === 'microsoft_todo') {
        newMappings.microsoft_todo = {
          userId: originalUser.userId || originalUser.id,
          email: originalUser.email || originalUser.userPrincipalName,
        };
      }
    } else {
      delete newMappings[appName];
    }
    setFormData({ ...formData, app_mappings: newMappings });
  };

  const handlePrefill = (source) => {
    let prefillData = { displayName: '', email: '' };

    if (source === 'jira' && formData.app_mappings?.jira) {
      const jiraUser = (mappableAccounts.jira_users || []).find(
        (u) => u.accountId === formData.app_mappings.jira.accountId,
      );
      if (jiraUser) {
        prefillData.displayName = jiraUser.displayName;
        prefillData.email = jiraUser.emailAddress || '';
      }
    } else if (source === 'confluence' && formData.app_mappings?.confluence) {
      const confUser = (mappableAccounts.confluence_users || []).find(
        (u) =>
          (u.accountId || u.id) === formData.app_mappings.confluence.accountId,
      );
      if (confUser) {
        prefillData.displayName = confUser.displayName;
        prefillData.email = confUser.email || confUser.emailAddress || '';
      }
    } else if (
      source === 'microsoft_teams' &&
      formData.app_mappings?.microsoft_teams
    ) {
      const teamsUser = (mappableAccounts.teams_users || []).find(
        (u) =>
          (u.userId || u.id) === formData.app_mappings.microsoft_teams.userId,
      );
      if (teamsUser) {
        prefillData.displayName = teamsUser.displayName;
        prefillData.email =
          teamsUser.email || teamsUser.userPrincipalName || '';
      }
    } else if (
      source === 'microsoft_outlook' &&
      formData.app_mappings?.microsoft_outlook
    ) {
      const outlookUser = (mappableAccounts.outlook_users || []).find(
        (u) =>
          (u.userId || u.id) === formData.app_mappings.microsoft_outlook.userId,
      );
      if (outlookUser) {
        prefillData.displayName = outlookUser.displayName || '';
        prefillData.email = outlookUser.email || '';
      }
    }

    setFormData((prev) => ({
      ...prev,
      displayName: prefillData.displayName || prev.displayName,
      email: prefillData.email || prev.email,
    }));
    toast.success(`Form pre-filled.`);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const url = user.id ? `${API_URL}/users/${user.id}` : `${API_URL}/users/`;
    const method = user.id ? 'PUT' : 'POST';

    try {
      const res = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        credentials: 'include',
        body: JSON.stringify(formData),
      });
      if (!res.ok) {
        // 409 sends the reason as text, 422 as a list of field errors.
        const { detail } = await res.json().catch(() => ({}));
        const reason = Array.isArray(detail)
          ? detail[0]?.msg?.replace(/^Value error, /, '')
          : detail;
        throw new Error(reason || 'Failed to save user.');
      }
      toast.success(`User saved.`);
      onSave();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const jiraOptions = (mappableAccounts.jira_users || []).map((u) => ({
    value: u.accountId,
    label: `${u.displayName} (${u.emailAddress || 'No Email'})`,
    originalUser: u,
  }));

  const confluenceOptions = (mappableAccounts.confluence_users || []).map(
    (u) => ({
      value: u.accountId || u.id,
      label: `${u.displayName} (${u.email || u.emailAddress || 'No Email'})`,
      originalUser: u,
    }),
  );

  const teamsOptions = (mappableAccounts.teams_users || []).map((u) => ({
    value: u.userId || u.id,
    label: `${u.displayName} (${u.email || u.userPrincipalName || 'No Email'})`,
    originalUser: u,
  }));

  const outlookOptions = (mappableAccounts.outlook_users || []).map((u) => ({
    value: u.userId || u.id,
    label: `${u.displayName || u.email} (${u.email || 'No Email'})`,
    originalUser: u,
  }));

  const hasAnyIntegration =
    ENABLE_INTEGRATION_JIRA ||
    ENABLE_INTEGRATION_CONFLUENCE ||
    ENABLE_INTEGRATION_MICROSOFT_TEAMS ||
    ENABLE_INTEGRATION_MICROSOFT_OUTLOOK ||
    ENABLE_INTEGRATION_MICROSOFT_TODO;

  return (
    <div className="animate-in fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4 backdrop-blur-sm duration-200">
      <div className="animate-in zoom-in-95 flex max-h-[90vh] w-full max-w-xl scale-100 flex-col overflow-y-auto rounded-xl border border-zinc-700 bg-[#121214] p-6 shadow-2xl">
        <div className="mb-5 flex items-center justify-between">
          <h3 className="text-lg font-bold text-zinc-100">
            {user.id ? 'Edit User' : 'Add User'}
          </h3>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-3">
            <div>
              <label
                htmlFor="display-name"
                className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-zinc-400"
              >
                Display Name
              </label>
              <input
                name="displayName"
                value={formData.displayName}
                onChange={handleChange}
                placeholder="Name"
                className="w-full rounded-md border border-zinc-700 bg-zinc-900 p-2 text-sm text-white transition-all focus:border-[#5652CF] focus:outline-none focus:ring-1 focus:ring-[#5652CF]/50"
                required
              />
            </div>

            <div>
              <label
                htmlFor="email"
                className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-zinc-400"
              >
                Email
              </label>
              <input
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Email"
                className={`w-full border bg-zinc-900 ${emailError ? 'border-red-500' : 'border-zinc-700'} rounded-md p-2 text-sm text-white transition-all focus:border-[#5652CF] focus:outline-none focus:ring-1`}
                required
              />
              {emailError && (
                <p className="mt-1 text-xs text-red-400">{emailError}</p>
              )}
            </div>

            <div>
              <label
                htmlFor="role"
                className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-zinc-400"
              >
                Role
              </label>
              <CreatableSelect
                styles={selectStyles}
                {...menuProps}
                options={roleOptions}
                value={
                  roleOptions.find((o) => o.value === formData.role) ||
                  (formData.role
                    ? { value: formData.role, label: formData.role }
                    : null)
                }
                onChange={handleRoleChange}
                formatCreateLabel={formatCreateLabel}
                placeholder="Select or type a role..."
                isClearable
              />
            </div>

            {hasAnyIntegration && (
              <div className="border-t border-zinc-800/50 pt-4">
                <h4 className="mb-3 text-xs font-bold uppercase tracking-wider text-zinc-300">
                  Linked Accounts
                </h4>

                <div className="space-y-3">
                  {ENABLE_INTEGRATION_JIRA && jiraOptions.length > 0 && (
                    <div>
                      <div className="mb-1 flex items-center gap-1.5 text-xs text-zinc-400">
                        <img
                          alt="Jira"
                          src={appImages.jira}
                          className="h-3.5 w-3.5 object-contain"
                        />{' '}
                        Jira
                      </div>
                      <Select
                        styles={selectStyles}
                        {...menuProps}
                        options={jiraOptions}
                        isClearable
                        value={jiraOptions.find(
                          (o) =>
                            o.value === formData.app_mappings?.jira?.accountId,
                        )}
                        onChange={(opt) => handleMappingChange('jira', opt)}
                        placeholder="Search Jira user..."
                      />
                      {formData.app_mappings?.jira && (
                        <button
                          type="button"
                          onClick={() => handlePrefill('jira')}
                          className="mt-1 flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300"
                        >
                          <ChevronsDown className="h-3 w-3" /> Prefill from Jira
                        </button>
                      )}
                    </div>
                  )}

                  {ENABLE_INTEGRATION_CONFLUENCE &&
                    confluenceOptions.length > 0 && (
                      <div>
                        <div className="mb-1 flex items-center gap-1.5 text-xs text-zinc-400">
                          <img
                            alt="Confluence"
                            src={appImages.confluence}
                            className="h-3.5 w-3.5 object-contain"
                          />{' '}
                          Confluence
                        </div>
                        <Select
                          styles={selectStyles}
                          {...menuProps}
                          options={confluenceOptions}
                          isClearable
                          value={confluenceOptions.find(
                            (o) =>
                              o.value ===
                              formData.app_mappings?.confluence?.accountId,
                          )}
                          onChange={(opt) =>
                            handleMappingChange('confluence', opt)
                          }
                          placeholder="Search Confluence user..."
                        />
                        {formData.app_mappings?.confluence && (
                          <button
                            type="button"
                            onClick={() => handlePrefill('confluence')}
                            className="mt-1 flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300"
                          >
                            <ChevronsDown className="h-3 w-3" /> Prefill from
                            Confluence
                          </button>
                        )}
                      </div>
                    )}

                  {ENABLE_INTEGRATION_MICROSOFT_TEAMS &&
                    teamsOptions.length > 0 && (
                      <div>
                        <div className="mb-1 flex items-center gap-1.5 text-xs text-zinc-400">
                          <img
                            alt="Microsoft Teams"
                            src={appImages.microsoft_teams}
                            className="h-3.5 w-3.5 object-contain"
                          />{' '}
                          Teams
                        </div>
                        <Select
                          styles={selectStyles}
                          {...menuProps}
                          options={teamsOptions}
                          isClearable
                          value={teamsOptions.find(
                            (o) =>
                              o.value ===
                              formData.app_mappings?.microsoft_teams?.userId,
                          )}
                          onChange={(opt) =>
                            handleMappingChange('microsoft_teams', opt)
                          }
                          placeholder="Search Teams user..."
                        />
                        {formData.app_mappings?.microsoft_teams && (
                          <button
                            type="button"
                            onClick={() => handlePrefill('microsoft_teams')}
                            className="mt-1 flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300"
                          >
                            <ChevronsDown className="h-3 w-3" /> Prefill from
                            Teams
                          </button>
                        )}
                      </div>
                    )}

                  {ENABLE_INTEGRATION_MICROSOFT_OUTLOOK &&
                    outlookOptions.length > 0 && (
                      <div>
                        <div className="mb-1 flex items-center gap-1.5 text-xs text-zinc-400">
                          <img
                            alt="Microsoft Outlook"
                            src={appImages.microsoft_outlook}
                            className="h-3.5 w-3.5 object-contain"
                          />{' '}
                          Outlook
                        </div>
                        <Select
                          styles={selectStyles}
                          {...menuProps}
                          options={outlookOptions}
                          isClearable
                          value={outlookOptions.find(
                            (o) =>
                              o.value ===
                              formData.app_mappings?.microsoft_outlook?.userId,
                          )}
                          onChange={(opt) =>
                            handleMappingChange('microsoft_outlook', opt)
                          }
                          placeholder="Search Outlook user..."
                        />
                        {formData.app_mappings?.microsoft_outlook && (
                          <button
                            type="button"
                            onClick={() => handlePrefill('microsoft_outlook')}
                            className="mt-1 flex items-center gap-1 text-[10px] text-blue-400 hover:text-blue-300"
                          >
                            <ChevronsDown className="h-3 w-3" /> Prefill from
                            Outlook
                          </button>
                        )}
                      </div>
                    )}
                </div>
              </div>
            )}
          </div>

          <div className="mt-6 flex justify-end gap-2 border-t border-zinc-800 pt-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !!emailError}
              className="rounded-md bg-[#5652CF] px-3 py-2 text-xs font-semibold text-white transition-all hover:bg-[#4642bf] disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : 'Save User'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- Compact Bulk Map Modal (Multi-Tool Sync) ---
const BulkMapModal = ({ allUsers, mappableAccounts, onClose, onSave }) => {
  const { userGoogleId } = DataState();
  const [candidates, setCandidates] = useState([]);
  const [defaultRole, setDefaultRole] = useState('Developer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState([]);

  useEffect(() => {
    const existingEmails = new Set(
      allUsers.map((u) => (u.email || '').toLowerCase()),
    );
    const existingTeamsIds = new Set(
      allUsers
        .map((u) => u.app_mappings?.microsoft_teams?.userId)
        .filter(Boolean),
    );
    const existingJiraIds = new Set(
      allUsers.map((u) => u.app_mappings?.jira?.accountId).filter(Boolean),
    );

    const mergedCandidatesMap = new Map();

    // 1. Process Jira Users
    (mappableAccounts?.jira_users || []).forEach((u) => {
      const email = (u.emailAddress || '').trim().toLowerCase();
      if (
        !email ||
        existingEmails.has(email) ||
        existingJiraIds.has(u.accountId)
      )
        return;

      if (!mergedCandidatesMap.has(email)) {
        mergedCandidatesMap.set(email, {
          displayName: u.displayName || email.split('@')[0],
          email: email,
          role: 'default',
          app_mappings: {},
          sources: new Set(),
        });
      }
      const cand = mergedCandidatesMap.get(email);
      cand.sources.add('jira');
      cand.app_mappings.jira = {
        accountId: u.accountId,
        displayName: u.displayName,
        emailAddress: email,
      };
    });

    // 2. Process Confluence Users
    (mappableAccounts?.confluence_users || []).forEach((u) => {
      const email = (u.email || u.emailAddress || '').trim().toLowerCase();
      const accountId = u.accountId || u.id;
      if (!email || existingEmails.has(email) || !accountId) return;

      if (!mergedCandidatesMap.has(email)) {
        mergedCandidatesMap.set(email, {
          displayName: u.displayName || email.split('@')[0],
          email: email,
          role: 'default',
          app_mappings: {},
          sources: new Set(),
        });
      }
      const cand = mergedCandidatesMap.get(email);
      cand.sources.add('confluence');
      cand.app_mappings.confluence = {
        accountId: accountId,
        displayName: u.displayName,
        email: email,
      };
    });

    // 3. Process Teams Users
    (mappableAccounts?.teams_users || []).forEach((u) => {
      const email = (u.email || u.userPrincipalName || '').trim().toLowerCase();
      const userId = u.userId || u.id;
      if (!email || existingEmails.has(email) || existingTeamsIds.has(userId))
        return;

      if (!mergedCandidatesMap.has(email)) {
        mergedCandidatesMap.set(email, {
          displayName: u.displayName || email.split('@')[0],
          email: email,
          role: 'default',
          app_mappings: {},
          sources: new Set(),
        });
      }
      const cand = mergedCandidatesMap.get(email);
      cand.sources.add('teams');
      cand.app_mappings.microsoft_teams = {
        userId: userId,
        displayName: u.displayName,
        email: email,
      };
    });

    // 4. Process Outlook Users
    (mappableAccounts?.outlook_users || []).forEach((u) => {
      const email = (u.email || '').trim().toLowerCase();
      const userId = u.userId || u.id;
      if (!email || existingEmails.has(email)) return;

      if (!mergedCandidatesMap.has(email)) {
        mergedCandidatesMap.set(email, {
          displayName: u.displayName || email.split('@')[0],
          email: email,
          role: 'default',
          app_mappings: {},
          sources: new Set(),
        });
      }
      const cand = mergedCandidatesMap.get(email);
      cand.sources.add('outlook');
      cand.app_mappings.microsoft_outlook = {
        userId: userId,
        email: email,
      };
    });

    const candidateList = Array.from(mergedCandidatesMap.values()).map((c) => ({
      ...c,
      sources: Array.from(c.sources),
      excluded: false,
    }));

    setCandidates(candidateList);
    // eslint-disable-next-line
  }, [allUsers, mappableAccounts]);

  const includedCandidates = useMemo(
    () => candidates.filter((c) => !c.excluded),
    [candidates],
  );

  const handleCandidateChange = (email, field, value) => {
    setCandidates((prev) =>
      prev.map((c) => (c.email === email ? { ...c, [field]: value } : c)),
    );
  };

  const handleToggleExclude = (email) => {
    setCandidates((prev) =>
      prev.map((c) =>
        c.email === email ? { ...c, excluded: !c.excluded } : c,
      ),
    );
  };

  const handleSubmit = async () => {
    if (includedCandidates.length === 0) {
      toast.error('No users selected to sync.');
      return;
    }
    setIsSubmitting(true);
    setValidationErrors([]);
    try {
      const payload = includedCandidates.map((c) => ({
        displayName: c.displayName,
        email: c.email,
        role: c.role === 'default' || !c.role ? defaultRole : c.role,
        app_mappings: c.app_mappings,
      }));

      const res = await fetch(`${API_URL}/users/bulk-sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${userGoogleId}`,
        },
        credentials: 'include',
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error('Validation failed.');
        setValidationErrors(data.detail || []);
        throw new Error('Validation failed');
      }
      toast.success(data.message);
      onSave();
    } catch (err) {
      // Error handled
    } finally {
      setIsSubmitting(false);
    }
  };

  const defaultRoleOptions = projectRoleOptions(allUsers);

  const candidateRoleOptions = [
    { value: 'default', label: 'Default' },
    ...defaultRoleOptions,
  ];

  return (
    <div className="animate-in fade-in fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-3 backdrop-blur-sm duration-200 md:p-6">
      <div className="animate-in zoom-in-95 flex h-[86vh] max-h-[92vh] w-[94vw] max-w-5xl scale-100 flex-col rounded-xl border border-zinc-700 bg-[#121214] shadow-2xl">
        <div className="flex items-center justify-between rounded-t-xl border-b border-zinc-800 bg-zinc-900/50 p-4">
          <div>
            <h3 className="text-lg font-bold text-zinc-100">
              Sync Users from Integrations
            </h3>
            <p className="text-xs text-zinc-500">
              Discovered {candidates.length} candidate users across connected
              tools.
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 text-zinc-500 transition-colors hover:bg-zinc-800 hover:text-zinc-200"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Controls Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-zinc-800 bg-zinc-900/30 px-4 py-3">
          <div className="text-xs text-zinc-400">
            Review candidate accounts discovered across your connected
            integrations.
          </div>

          <div className="flex items-center gap-2">
            <label
              htmlFor="default-role"
              className="whitespace-nowrap text-xs font-medium text-zinc-400"
            >
              Default Role:
            </label>
            <div className="w-48">
              <CreatableSelect
                styles={selectStyles}
                {...menuProps}
                options={defaultRoleOptions}
                value={{ value: defaultRole, label: defaultRole }}
                onChange={(opt) =>
                  setDefaultRole(opt ? opt.value : 'Developer')
                }
                formatCreateLabel={formatCreateLabel}
              />
            </div>
          </div>
        </div>

        <div className="custom-scrollbar flex-grow overflow-auto p-0">
          <table className="w-full border-collapse text-left text-xs">
            <thead className="sticky top-0 z-10 bg-zinc-900">
              <tr>
                <th className="px-4 py-2 font-semibold uppercase tracking-wider text-zinc-500">
                  Display Name
                </th>
                <th className="px-4 py-2 font-semibold uppercase tracking-wider text-zinc-500">
                  Email
                </th>
                <th className="px-4 py-2 font-semibold uppercase tracking-wider text-zinc-500">
                  Connected Apps
                </th>
                <th className="px-4 py-2 font-semibold uppercase tracking-wider text-zinc-500">
                  Role
                </th>
                <th className="px-4 py-2 text-center font-semibold uppercase tracking-wider text-zinc-500">
                  Status
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-800">
              {candidates.length === 0 ? (
                <tr>
                  <td colSpan="5" className="p-8 text-center text-zinc-500">
                    No new candidates found.
                  </td>
                </tr>
              ) : (
                candidates.map((candidate, index) => {
                  const error = validationErrors.find(
                    (e) => e.email === candidate.email,
                  );
                  const isExcluded = candidate.excluded;
                  return (
                    <React.Fragment key={candidate.email}>
                      <tr
                        className={`group transition-all ${
                          isExcluded
                            ? 'bg-red-950/10 opacity-45'
                            : 'hover:bg-zinc-800/30'
                        }`}
                      >
                        <td className="p-2 pl-4">
                          <input
                            value={candidate.displayName}
                            disabled={isExcluded}
                            onChange={(e) =>
                              handleCandidateChange(
                                candidate.email,
                                'displayName',
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-transparent bg-zinc-800/50 px-2 py-1 text-zinc-200 transition-colors focus:border-zinc-600 focus:outline-none disabled:opacity-50"
                          />
                        </td>
                        <td className="p-2">
                          <span
                            className={`font-mono ${
                              isExcluded
                                ? 'text-zinc-500 line-through'
                                : 'text-zinc-300'
                            }`}
                          >
                            {candidate.email}
                          </span>
                        </td>
                        <td className="p-2">
                          <div className="flex flex-wrap gap-1">
                            {Object.entries(candidate.app_mappings || {}).map(
                              ([appKey, mapData]) => (
                                <AppBadge
                                  key={appKey}
                                  appKey={appKey}
                                  data={mapData}
                                />
                              ),
                            )}
                          </div>
                        </td>
                        <td className="min-w-[170px] p-2">
                          <CreatableSelect
                            styles={selectStyles}
                            {...menuProps}
                            isDisabled={isExcluded}
                            options={candidateRoleOptions}
                            value={
                              candidate.role === 'default' || !candidate.role
                                ? {
                                    value: 'default',
                                    label: 'Default',
                                  }
                                : {
                                    value: candidate.role,
                                    label: candidate.role,
                                  }
                            }
                            onChange={(opt) =>
                              handleCandidateChange(
                                candidate.email,
                                'role',
                                opt ? opt.value : 'default',
                              )
                            }
                            formatCreateLabel={formatCreateLabel}
                          />
                        </td>
                        <td className="p-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleToggleExclude(candidate.email)}
                            className={`inline-flex items-center gap-1.5 rounded-md border px-2.5 py-1 text-[11px] font-medium transition-all active:scale-95 ${
                              isExcluded
                                ? 'border-red-500/40 bg-red-500/15 text-red-400 hover:border-red-500/60 hover:bg-red-500/25'
                                : 'border-emerald-500/40 bg-emerald-500/15 text-emerald-400 hover:border-emerald-500/60 hover:bg-emerald-500/25'
                            }`}
                            title={
                              isExcluded
                                ? 'Excluded (click to include)'
                                : 'Included (click to exclude)'
                            }
                          >
                            {isExcluded ? (
                              <>
                                <XCircle className="h-3.5 w-3.5 text-red-400" />
                                <span>Excluded</span>
                              </>
                            ) : (
                              <>
                                <CheckCircle2 className="h-3.5 w-3.5 text-emerald-400" />
                                <span>Include</span>
                              </>
                            )}
                          </button>
                        </td>
                      </tr>
                      {error && (
                        <tr className="border-b border-red-900/20 bg-red-900/10">
                          <td
                            colSpan="5"
                            className="flex items-center gap-2 px-4 py-1 text-[10px] text-red-400"
                          >
                            <X className="h-3 w-3" /> {error.errors.join(' ')}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between rounded-b-xl border-t border-zinc-800 bg-zinc-900 p-4">
          <p className="text-xs font-medium text-zinc-400">
            <strong className="font-semibold text-emerald-400">
              {includedCandidates.length}
            </strong>{' '}
            of{' '}
            <strong className="font-semibold text-zinc-200">
              {candidates.length}
            </strong>{' '}
            users selected to sync.
          </p>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-zinc-700 bg-zinc-800 px-3 py-2 text-xs font-semibold text-zinc-300 transition-colors hover:bg-zinc-700"
            >
              Cancel
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || includedCandidates.length === 0}
              className="flex items-center gap-2 rounded-md bg-[#5652CF] px-3 py-2 text-xs font-semibold text-white transition-all hover:bg-[#4642bf] disabled:opacity-50"
            >
              {isSubmitting ? (
                <RefreshCw className="h-3.5 w-3.5 animate-spin" />
              ) : (
                <CheckCircle2 className="h-3.5 w-3.5" />
              )}
              {isSubmitting
                ? 'Processing...'
                : `Create (${includedCandidates.length})`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default UserManagementContent;
